import React from 'react';
import { Badge, Button, Table } from '@syneto/compass-react';
import Preloader from '../../../../components/preloader';
import { COMPLIANCE_VERDICT_STATUS, COMPLIANCE_VERDICTS_SIZE_PER_PAGE_LIST } from '../../../../constants/compliance-verdicts';
import { NOT_AVAILABLE } from '../../../../constants/hardware-inventory';
import { statusAppearance, statusLabel, verdictDateLabel, verdictScopeLabel, verdictVersionLabel } from './complianceVerdictDisplay';

const ComplianceVerdictsTable = (props) => {
	const {
		verdicts,
		pagination,
		componentTypes,
		componentTypesLoading,
		isLoading,
		hasError,
		isSaving,
		statusFilter,
		loadedSerialNumber,
		onPaginationChange,
		onGrantVerdict,
		onRevokeVerdict
	} = props;

	/*
	 * Only pagination comes back through the table - the filters live in the bar above so they can
	 * reset the offset at the same time.
	 */
	const handleTableChange = (type, { page, sizePerPage }) => {
		if (type !== 'pagination') {
			return;
		}

		const start = (page - 1) * sizePerPage;

		onPaginationChange(start >= 0 ? start : 0, sizePerPage);
	};

	const renderValue = (cell) => {
		return cell ?? NOT_AVAILABLE;
	};

	const renderScope = (cell, row) => {
		return verdictScopeLabel(row, componentTypes, componentTypesLoading);
	};

	const renderVersion = (cell, row) => {
		return verdictVersionLabel(row);
	};

	/*
	 * When the verdict was recorded - the failed check, or the override created directly - not when
	 * it last changed status. The API lists newest first, so this is what reads an appliance's
	 * verdicts as a history.
	 */
	const renderCreatedAt = (cell) => {
		return verdictDateLabel(cell);
	};

	const renderStatus = (cell) => {
		if (!cell) {
			return NOT_AVAILABLE;
		}

		return <Badge appearance={statusAppearance(cell)}>{statusLabel(cell)}</Badge>;
	};

	/*
	 * One action per status, and none for a revoked row: both transitions are one-way, and an absent
	 * control is unambiguous where a disabled one invites "why?". The buttons go dark while any write
	 * is in flight, which is what stops a second write racing the first against one list refresh, and
	 * while a read is in flight, because the rows kept on screen through a page or status change are
	 * the previous answer rather than the one being asked for.
	 */
	const renderActions = (cell, row) => {
		if (row.status === COMPLIANCE_VERDICT_STATUS.NEW) {
			return (
				<Button role="primary" disabled={isSaving || isLoading} onClick={() => { return onGrantVerdict(row); }}>Grant override</Button>
			);
		}

		if (row.status === COMPLIANCE_VERDICT_STATUS.ACTIVE) {
			return (
				<Button role="tertiary" disabled={isSaving || isLoading} onClick={() => { return onRevokeVerdict(row); }}>Revoke</Button>
			);
		}

		return null;
	};

	/*
	 * The empty messages key off `loadedSerialNumber` - the serial filter the rows were actually
	 * answered for - so they cannot name an appliance whose request has not come back.
	 */
	const emptyTableState = () => {
		if (isLoading) {
			return <Preloader dataIsLoading={true} />;
		}

		if (hasError) {
			return 'Compliance verdicts could not be loaded.';
		}

		const forAppliance = loadedSerialNumber ? ` for ${loadedSerialNumber}` : '';

		if (statusFilter === COMPLIANCE_VERDICT_STATUS.NEW) {
			return `No failed compliance checks awaiting a decision${forAppliance}.`;
		}

		if (statusFilter === COMPLIANCE_VERDICT_STATUS.ACTIVE) {
			return `No active overrides${forAppliance}.`;
		}

		if (statusFilter === COMPLIANCE_VERDICT_STATUS.REVOKED) {
			return `No overrides have been revoked${forAppliance}.`;
		}

		return `No compliance verdicts recorded${forAppliance}.`;
	};

	return (
		<Table
			data={verdicts ?? []}
			keyField="id"
			remote
			pagination={{
				sizePerPageList: COMPLIANCE_VERDICTS_SIZE_PER_PAGE_LIST,
				sizePerPage: pagination?.itemsPerPage,
				showTotal: true,
				page: pagination?.currentPage,
				totalSize: pagination?.totalItems
			}}
			onTableChange={handleTableChange}
			noDataIndication={emptyTableState()}
		>
			<Table.Col field="serialNumber" align="left" renderCell={renderValue}>Serial number</Table.Col>
			<Table.Col field="hardwareComponentTypeId" align="left" renderCell={renderScope}>Scope</Table.Col>
			<Table.Col field="firmwareVersion" align="left" renderCell={renderVersion}>Firmware version</Table.Col>
			<Table.Col field="createdAt" align="left" renderCell={renderCreatedAt}>Created</Table.Col>
			<Table.Col field="status" align="left" renderCell={renderStatus}>Status</Table.Col>
			<Table.Col field="actions" align="left" renderCell={renderActions}>Actions</Table.Col>
		</Table>
	);
};

export default ComplianceVerdictsTable;
